import { useAnalyticsEvent } from 'components/GoogleAnalytics';
import React, { PropsWithChildren } from 'react';
import ReactModal, { Props as ReactModalProps } from 'react-modal';

import { mergeClasses } from '@skippay/core/utils/classUtils';
import './indexNew.sass';

import { getELabFromClick } from './utils';

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

export type ModalProps = ReactModalProps & {
	size?: ModalSize;
	align?: 'top' | 'center';
	displayOnTop?: boolean;
	contentLabel: string;
	onAfterOpen?: () => void;
	onClose?: () => void;
	shouldCloseOnOverlayClick?: boolean;
	shouldCloseOnEsc?: boolean;
	isOpen?: boolean;
	maxHeight?: 'screen' | 'none';
	className?: string;
};

const SIZE_CLASSES: Record<ModalSize, string> = {
	xs: 'max-w-[24.375rem]', // 390px
	sm: 'max-w-[31.25rem]', // 500px
	md: 'max-w-[37.5rem]', // 600px
	lg: 'max-w-[50rem]', // 800px
	xl: 'max-w-[64rem]', // 1024px
	full: 'w-full h-screen max-w-full max-h-screen',
};

const Modal = ({
	size = 'lg',
	align = 'center',
	children,
	contentLabel,
	displayOnTop,
	onAfterOpen,
	isOpen,
	maxHeight = 'screen',
	className,
	onClose,
	shouldCloseOnOverlayClick = true,
	shouldCloseOnEsc = true,
	...other
}: PropsWithChildren<ModalProps>) => {
	const sendEvent = useAnalyticsEvent({ eAct: contentLabel, eCat: 'Modal Window', eVal: 1 });

	return (
		<ReactModal
			{...other}
			isOpen={isOpen}
			contentLabel={contentLabel}
			className={mergeClasses(
				'dark:bg-light-gray-50 fixed w-[calc(100%-2rem)] transform bg-white text-center',
				align === 'top' ? 'top-5 translate-y-0' : 'top-1/2',
				align === 'center' && 'translate-y-[-50%]',
				size === 'full'
					? 'Modal--fullScreen top-0! left-0! translate-x-0 translate-y-0 rounded-none!'
					: 'left-1/2 -translate-x-1/2 rounded-2xl',
				SIZE_CLASSES[size],
				maxHeight === 'screen' &&
					'max-h-[calc(100vh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))]',
				className
			)}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			shouldCloseOnEsc={shouldCloseOnEsc}
			portalClassName={mergeClasses(
				'ReactModalPortal',
				displayOnTop ? 'z-2147483647' : 'z-2147483646'
			)}
			overlayClassName={mergeClasses('fixed inset-0 bg-black/50 flex items-center justify-center')}
			ariaHideApp={process.env.NODE_ENV !== 'test'}
			onRequestClose={onClose}
			onAfterOpen={() => {
				onAfterOpen?.();
				sendEvent({ eInt: true, eLab: 'Modal Shown' });
			}}
			closeTimeoutMS={300}
		>
			<div
				onClick={({ target }) => {
					const eLab = getELabFromClick(target);
					if (eLab) {
						sendEvent({ eInt: false, eLab });
					}
				}}
			>
				{children}
			</div>
		</ReactModal>
	);
};

export default Modal;
